
import bus from 'public/utils/bus'
import { createWatermarkStyle, getToken } from 'public/utils'
import { LOG_PREFIX, TOKENS, BUILTIN_THEMES, THEME_KEY, CONTRAST_CONFIG, SAVE_CONFIG, DEFAULT_CONFIG } from './config'
import { isPlainObject, toCssVarName } from './utils'
import { getThemeConfig, saveThemeConfig, deleteThemeConfig, reportUserAction } from './http'

export class ThemeSystem {
  constructor(
    {
      target = document.documentElement,
      builtinThemes = BUILTIN_THEMES,
      contrastConfig = CONTRAST_CONFIG,
      themeKey = THEME_KEY
    } = {}
  ) {
    this._target = target
    this._skins = new Map()
    this._skinId = null
    this._contrastConfig = contrastConfig
    this._conterast = null
    this._themeKey = themeKey
    this._getFromApi = false // 是否从接口获取
    this._assetFields = [TOKENS.HEADER_BG_IMAGE] // 定义哪些根级别字段需要转 CSS 变量

    // 注册内置主题
    this._registerBuiltins(builtinThemes)

    // 初始化
    this.init()

    console.info(`${LOG_PREFIX}初始化`, this)
  }

  /**
   * 初始化主题系统
   */
  init() {
    // 应用所有配置
    this._apply()

    // 异步获取接口配置，有则替换
    if (getToken()) {
      this._getToApi()
    }
  }

  /**
   * 注册皮肤
   * @param {string} skinId - 皮肤ID
   * @param {Object} config - 皮肤配置
   * @param {string} config.label - 皮肤标签
   * @param {Object} config.tokens - 皮肤tokens
   * @param {Object} options - 选项
   * @param {boolean} options.override - 是否覆盖
   * @returns {boolean} 是否成功注册
   */
  registerSkin(skinId, config, { override = true } = {}) {
    const id = String(skinId || '').trim()
    if (!id || !config || !isPlainObject(config.tokens)) {
      return false
    }
    if (!override && this._skins.has(id)) {
      return false
    }

    this._skins.set(id, {
      id,
      label: config.label || id,
      tokens: { ...config.tokens }
    })

    return true
  }

  /**
   * 上报用户操作
   * @param {string} [key] - 操作键名，可选，默认上报所有操作
   * @returns {Object} 皮肤对象
   */
  reportUserAction(key) {
    key && reportUserAction({ [key]: 1 })
  }

  /**
   * 保存配置对象
   * @param {Object} data - 配置对象
   * @returns {Object} 配置对象
   */
  saveConfig(data = DEFAULT_CONFIG) {
    const config = this._getConfig()
    const skinId = data[SAVE_CONFIG.SKIN_ID] || config[SAVE_CONFIG.SKIN_ID]
    const contrast = data[SAVE_CONFIG.CONTRAST] || config[SAVE_CONFIG.CONTRAST]
    const newConfig = {
      ...config,
      ...data,
      [SAVE_CONFIG.SKIN_ID]: skinId,
      [SAVE_CONFIG.CONTRAST]: contrast
    }
    this._applySkin(skinId)
    this._applyContrast(contrast)
    this._saveConfig(newConfig)
  }

  /**
   * 删除配置对象
   */
  removeConfig(employeeId) {
    this._removeConfig(employeeId)
  }

  /**
   * 重置配置对象
   */
  resetConfig() {
    const newConfig = { ...DEFAULT_CONFIG }
    this._saveConfig(newConfig)
  }

  /**
   * 获取配置对象
   * @param {string} [field] - 可选，指定获取某个字段
   * @returns {Object|any} 配置对象或字段值
   */
  getConfig(field) {
    const config = this._getConfig()
    return field ? config[field] : config
  }

  /**
   * 更新配置字段
   * @param {string} field - 字段名
   * @param {any} value - 字段值
   */
  updateConfig(field, value) {
    const config = this._getConfig()
    config[field] = value
    config[SAVE_CONFIG.UPDATE_TIME] = Date.now()
    this._saveConfig(config)
  }

  /**
   * 是否保存配置对象
   * @returns {boolean} 是否保存
   */
  async isSave() {
    try {
      const config = await this._getToApi()
      return config && !!config.id
    } catch (e) {
      console.warn(`${LOG_PREFIX}接口获取配置失败`, e)
      return true
    }
  }

  // ========== 私有方法 ==========

  /**
   * 注册内置主题
   */
  _registerBuiltins(builtinThemes) {
    if (!Array.isArray(builtinThemes)) return

    builtinThemes.forEach(theme => {
      const id = theme.value || theme.label
      if (!id) return

      this.registerSkin(id, {
        label: theme.label || id,
        tokens: theme.tokens
      })
    })
  }

  _apply() {
    const config = this._getConfig()
    const savedSkin = config[SAVE_CONFIG.SKIN_ID] || DEFAULT_CONFIG[SAVE_CONFIG.SKIN_ID]
    const savedContrast = config[SAVE_CONFIG.CONTRAST] || DEFAULT_CONFIG[SAVE_CONFIG.CONTRAST]
    // 皮肤
    this._applySkin(savedSkin)
    // 对比度
    this._applyContrast(savedContrast)
  }

  /**
   * 应用/切换皮肤
   * @param {string} skinId - 皮肤ID
   * @returns {Promise<boolean>} 是否成功
   */
  _applySkin(skinId) {
    const id = String(skinId || '').trim()
    if (!id) return false

    // 获取皮肤配置
    const skin = this._skins.get(id)
    if (!skin) {
      console.error(`${LOG_PREFIX}皮肤未注册`)
      return false
    }
    // 应用 tokens
    this._applyTokens(skin.tokens)

    // 更新内存状态
    this._skinId = id

    return true
  }

  /**
   * 应用对比度
   * @param {string} contrast - 对比度值
   * @returns {Promise boolean} 是否成功
   */
  _applyContrast(contrast) {
    const id = +contrast
    if (!id) return false

    // 获取对比度配置
    const contrastConfig = this._contrastConfig.includes(id)
    if (!contrastConfig) {
      console.error(`${LOG_PREFIX}对比度未注册`)
      return false
    }

    // 应用对比度
    this._applyContrastHtml(id)

    // 更新内存状态
    this._conterast = id

    return true
  }

  /**
   * 获取本地存储的完整配置对象
   */
  _getConfig() {
    try {
      const raw = localStorage.getItem(this._themeKey)
      return raw ? JSON.parse(raw) : {}
    } catch (e) {
      console.error(`${LOG_PREFIX}读取配置失败`, e)
      return {}
    }
  }

  /**
   * 获取本地存储的完整配置对象
   */
  _getToLocalStorage() {
    try {
      const raw = localStorage.getItem(this._themeKey)
      return raw ? JSON.parse(raw) : {}
    } catch (e) {
      console.error(`${LOG_PREFIX}读取本地配置失败`, e)
      return {}
    }
  }

  /**
   * 获取接口的完整配置对象，同步保存到本地存储
   */
  async _getToApi() {
    if (!this._getFromApi) {
      this._getFromApi = getThemeConfig()
    }
    const data = await this._getFromApi
    let config = {
      [SAVE_CONFIG.ID]: data.id,
      [SAVE_CONFIG.EMPLOYEE_ID]: data.employeeId
    }
    if (data && data.id) {
      config = Object.values(SAVE_CONFIG).reduce((acc, key) => {
        acc[key] = data[key]
        return acc
      }, {})
      // 校验id是否在配置列表中，如果不在，则取默认配置
      if (!this._skins.has(config[SAVE_CONFIG.SKIN_ID])) {
        config[SAVE_CONFIG.SKIN_ID] = DEFAULT_CONFIG[SAVE_CONFIG.SKIN_ID]
      }
    }
    this._saveToLocalStorage(config)

    this._apply()

    return config
  }

  /**
   * 保存配置对象到本地存储
   */
  _saveConfig(config) {
    try {
      let _config = {}
      Object.values(SAVE_CONFIG).forEach(key => {
        _config[key] = config[key]
      })

      // 保存到 localStorage
      this._saveToLocalStorage(_config)

      // 异步保存到接口（不阻塞）
      this._saveToApi(_config)

      bus.$emit(THEME_KEY, _config)
    } catch (e) {
      console.error(`${LOG_PREFIX}保存配置失败`, e)
    }
  }

  /**
   * 保存到 localStorage
   */
  _saveToLocalStorage(config) {
    try {
      let _config = {
        [SAVE_CONFIG.ID]: config[SAVE_CONFIG.ID] || '',
        [SAVE_CONFIG.SKIN_ID]: config[SAVE_CONFIG.SKIN_ID] || DEFAULT_CONFIG[SAVE_CONFIG.SKIN_ID],
        [SAVE_CONFIG.CONTRAST]: config[SAVE_CONFIG.CONTRAST] || DEFAULT_CONFIG[SAVE_CONFIG.CONTRAST],
        [SAVE_CONFIG.UPDATE_TIME]: Date.now(),
        [SAVE_CONFIG.CREATE_TIME]: config[SAVE_CONFIG.CREATE_TIME] || Date.now(),
        [SAVE_CONFIG.EMPLOYEE_ID]: config[SAVE_CONFIG.EMPLOYEE_ID] || localStorage.ERPCacheFlag || '',
        [SAVE_CONFIG.SHOW_DIGITAL_HUMAN]: config[SAVE_CONFIG.SHOW_DIGITAL_HUMAN] !== undefined ? config[SAVE_CONFIG.SHOW_DIGITAL_HUMAN] : DEFAULT_CONFIG[SAVE_CONFIG.SHOW_DIGITAL_HUMAN]
      }

      localStorage.setItem(this._themeKey, JSON.stringify(_config))
    } catch (e) {
      console.error(`${LOG_PREFIX}保存本地配置失败`, e)
    }
  }

  /**
   * 异步保存到接口
   */
  async _saveToApi(config) {
    try {
      saveThemeConfig({
        [SAVE_CONFIG.SKIN_ID]: config[SAVE_CONFIG.SKIN_ID],
        [SAVE_CONFIG.CONTRAST]: config[SAVE_CONFIG.CONTRAST],
        [SAVE_CONFIG.SHOW_DIGITAL_HUMAN]: config[SAVE_CONFIG.SHOW_DIGITAL_HUMAN]
      })
    } catch (e) {
      console.warn(`${LOG_PREFIX}保存接口配置失败`, e)
    }
  }

  /**
   * 删除配置对象（双删：接口 + localStorage）
   */
  _removeConfig(employeeId) {
    this._removeToLocalStorage()
    this._removeToApi(employeeId)
  }

  /**
   * 删除本地存储配置
   */
  _removeToLocalStorage() {
    try {
      localStorage.removeItem(this._themeKey)
    } catch (e) {
      console.error(`${LOG_PREFIX}删除本地配置失败`, e)
    }
  }

  /**
   * 删除接口配置
   */
  _removeToApi(employeeId) {
    try {
      deleteThemeConfig({ employeeId })
    } catch (e) {
      console.warn(`${LOG_PREFIX}删除接口配置失败`, e)
    }
  }

  /**
   * 应用 tokens（内部使用，带性能优化）
   */
  _applyTokens(tokens) {
    if (!isPlainObject(tokens) || !this._target || !this._target.style) return

    const fragment = {}
    Object.entries(tokens).forEach(([key, value]) => {
      // if (value === undefined || value === null) return
      const varName = toCssVarName(key)
      if (varName) {
        fragment[varName] = String(value)
      }

      if (this._assetFields.includes(key)) {
        fragment[varName] = value ? `url('${value}')` : ''
      }
    })

    // 批量设置（减少重绘）
    this._batchSetCSSVars(fragment)
  }

  /**
   * 批量设置CSS变量
   */
  _batchSetCSSVars(vars) {
    const target = this._target
    if (!target) return

    // 使用 requestAnimationFrame 优化
    requestAnimationFrame(() => {
      Object.entries(vars).forEach(([name, value]) => {
        target.style.setProperty(name, value)
      })
    })
  }

  /**
   * 应用对比度HTML样式
   */
  _applyContrastHtml(contrast) {
    const doms = document.querySelectorAll('.hc_extension_rgb')
    const reverseDoms = document.querySelectorAll('.hc_extension_reverse_rgb')
    if (doms && doms.length) {
      doms.forEach(v => v.setAttribute('exponent', contrast))
    }
    if (reverseDoms && reverseDoms.length) {
      // 取倒数，反转对比度
      reverseDoms.forEach(v => v.setAttribute('exponent', (1 / contrast).toFixed(2)))
    }
    document.documentElement.setAttribute('hc', contrast)
    if (window.__ERPUSER__ && window.__ERPUSER__.name) {
      createWatermarkStyle(window.__ERPUSER__, contrast)
    }
  }
}

export const themeSystem = new ThemeSystem()



// WEBPACK FOOTER //
// ./src/layout/layout/setup/theme-system/index.js